/**
 * V68: getIdleWorkforceMonthly + updateIdleWorkforceNote hook
 */
import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export interface IdleEmployee {
  userId: string;
  nameKo: string;
  orgUnitId: string;
  orgUnitName: string;
  companyCode: 'KR' | 'CN';
  employmentTypeCode: string;
  employmentTypeName: string;
  status: 'active' | 'on_leave';
  autoRemark: string;
  manualNote: string;
}

export interface IdleResponse {
  success: boolean;
  yearMonth: string;
  idleEmployees: IdleEmployee[];
  error?: string;
}

export function useIdleWorkforce(yearMonth?: string) {
  const [data, setData] = useState<IdleEmployee[]>([]);
  const [resolvedYearMonth, setResolvedYearMonth] = useState<string | undefined>(yearMonth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<{ yearMonth?: string }, IdleResponse>(getFunctions(), 'getIdleWorkforceMonthly');
        const r = await fn({ yearMonth });
        if (cancelled) return;
        if (r.data.success) {
          setData(r.data.idleEmployees || []);
          setResolvedYearMonth(r.data.yearMonth);
        } else {
          setError(r.data.error || '조회 실패');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '서버 오류');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [yearMonth, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);
  return { data, yearMonth: resolvedYearMonth, loading, error, refresh };
}

export function useUpdateIdleWorkforceNote() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useCallback(async (yearMonth: string, userId: string, note: string) => {
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable<{ yearMonth: string; userId: string; note: string }, { success: boolean }>(
        getFunctions(), 'updateIdleWorkforceNote'
      );
      const r = await fn({ yearMonth, userId, note });
      return r.data.success === true;
    } catch (e: any) {
      setError(e?.message || '저장 실패');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);
  return { save, saving, error };
}
